import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

import { ContributionDetailDialog } from '@/components/giving/contribution-detail-dialog';
import { VoidReasonDialog } from '@/components/giving/void-reason-dialog';
import { FundAdjustBalanceDialog } from '@/components/giving/fund-adjust-balance-dialog';
import { RecordContributionDialog } from '@/components/giving/record-contribution-dialog';
import {
  DateRangeFilter,
  LIST_FILTER_DATE_RANGE_PRESETS,
  resolvePreset,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { FilterBar } from '@/components/shared/filter-bar';
import { Pagination } from '@/components/shared/pagination';
import { SearchInput } from '@/components/shared/search-input';
import { StatCard } from '@/components/shared/stat-card';
import { Tip } from '@/components/shared/tip';
import { RecordExpenseDialog } from '@/components/spending/record-expense-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { formatDate, formatDateTime } from '@/utils/format';
import { CONTRIBUTION_METHODS } from '@/lib/contribution-methods';

const METHOD_ANY = 'any';

export interface FundDetail {
  id: string;
  name: string;
  code: string;
  fundType: string;
  customTypeId: string | null;
  customTypeName: string | null;
  isActive: boolean;
  acceptsPledges: boolean;
  availableForPrograms: boolean;
  recordGiverIdentity: boolean;
  defaultCurrency: string;
  givenMinor: number;
  givenCount: number;
  spentMinor: number;
  spentCount: number;
  balanceMinor: number;
}

interface FundTransaction {
  id: string;
  memberId: string | null;
  giverName: string | null;
  identityHidden: boolean;
  amountMinor: number;
  currency: string;
  contributionDate: string;
  method: string;
  receiptNumber: number;
  note: string | null;
  isVoided: boolean;
  voidReason: string | null;
  voidedByName: string | null;
  canVoid: boolean;
}

interface FundBalanceAdjustment {
  id: string;
  previousBalanceMinor: number;
  newBalanceMinor: number;
  currency: string;
  reason: string;
  adjustedByUserId: string;
  adjustedByName: string;
  createdAt: string;
}

interface OffsetPage {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

interface FundDetailContextValue {
  fund: FundDetail;
  canRecordGift: boolean;
  canRecordExpense: boolean;
  canAdjustBalance: boolean;
  openRecordGift: () => void;
  openRecordExpense: () => void;
  openAdjustBalance: () => void;
}

const FundDetailContext = createContext<FundDetailContextValue | null>(null);

const useFundDetailContext = () => {
  const value = useContext(FundDetailContext);
  if (!value) {
    throw new Error('Fund detail controls must be used inside FundDetailProvider');
  }
  return value;
};

export const FundDetailProvider = ({
  fund,
  children,
}: {
  fund: FundDetail;
  children: ReactNode;
}) => {
  const membership = useChurchStore((state) => state.membership);
  const isModuleEnabled = useChurchStore((state) => state.isModuleEnabled);
  const spendingEnabled = isModuleEnabled('spending');
  const [recordGiftOpen, setRecordGiftOpen] = useState(false);
  const [recordExpenseOpen, setRecordExpenseOpen] = useState(false);
  const [adjustBalanceOpen, setAdjustBalanceOpen] = useState(false);
  const { t } = useTranslation('giving');

  const canRecordGift = hasPermission(membership, PERMISSIONS.GIVING_RECORD);
  const canRecordExpense =
    spendingEnabled && hasPermission(membership, PERMISSIONS.EXPENSES_CREATE);
  const canAdjustBalance = hasPermission(membership, PERMISSIONS.GIVING_ADJUST_BALANCE);

  const value = useMemo(
    () => ({
      fund,
      canRecordGift,
      canRecordExpense,
      canAdjustBalance,
      openRecordGift: () => setRecordGiftOpen(true),
      openRecordExpense: () => setRecordExpenseOpen(true),
      openAdjustBalance: () => setAdjustBalanceOpen(true),
    }),
    [canAdjustBalance, canRecordExpense, canRecordGift, fund],
  );

  return (
    <FundDetailContext.Provider value={value}>
      {children}
      <RecordContributionDialog
        open={recordGiftOpen}
        onOpenChange={setRecordGiftOpen}
        fundId={fund.id}
        fundName={fund.name}
        currency={fund.defaultCurrency}
        title={t('funds.detail.recordGift')}
      />
      <RecordExpenseDialog
        open={recordExpenseOpen}
        onOpenChange={setRecordExpenseOpen}
      />
      <FundAdjustBalanceDialog
        open={adjustBalanceOpen}
        onOpenChange={setAdjustBalanceOpen}
        fundId={fund.id}
        fundName={fund.name}
        currency={fund.defaultCurrency}
        currentBalanceMinor={fund.balanceMinor}
      />
    </FundDetailContext.Provider>
  );
};

/** Record gift / expense buttons for dialog or page headers. */
export const FundDetailActions = ({ trailing }: { trailing?: ReactNode }) => {
  const { t } = useTranslation('giving');
  const {
    fund,
    canRecordGift,
    canRecordExpense,
    canAdjustBalance,
    openRecordGift,
    openRecordExpense,
    openAdjustBalance,
  } = useFundDetailContext();

  if (!trailing && !canRecordGift && !canRecordExpense && !canAdjustBalance) return null;

  return (
    <div className="flex shrink-0 flex-wrap justify-end gap-2">
      {canAdjustBalance ? (
        <Button type="button" size="sm" variant="outline" onClick={openAdjustBalance}>
          {t('funds.detail.adjustBalance.action')}
        </Button>
      ) : null}
      {canRecordExpense ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!fund.isActive}
          onClick={openRecordExpense}
        >
          {t('funds.detail.recordExpense')}
        </Button>
      ) : null}
      {canRecordGift ? (
        <Button
          type="button"
          size="sm"
          disabled={!fund.isActive}
          onClick={openRecordGift}
        >
          {t('funds.detail.recordGift')}
        </Button>
      ) : null}
      {trailing}
    </div>
  );
};

export const FundDetailPanel = ({
  fund,
  popoverPortal,
}: {
  fund: FundDetail;
  popoverPortal?: 'dialog';
}) => {
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const timezone = usePreferences((state) => state.timezone) ?? church?.defaultTimezone ?? 'UTC';

  const [detailTab, setDetailTab] = useState<'gifts' | 'types' | 'adjustments'>('gifts');
  const [txPage, setTxPage] = useState(1);
  const [txSearch, setTxSearch] = useState('');
  const [txMethod, setTxMethod] = useState<string>(METHOD_ANY);
  const [txDateRange, setTxDateRange] = useState<DateRange>(() => ({
    preset: 'allTime',
    ...resolvePreset('allTime', timezone),
  }));
  const [detailContributionId, setDetailContributionId] = useState<string | null>(null);
  const [voidTarget, setVoidTarget] = useState<FundTransaction | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidPassword, setVoidPassword] = useState('');
  const [reasonTarget, setReasonTarget] = useState<FundTransaction | null>(null);
  const [adjustmentPage, setAdjustmentPage] = useState(1);

  useEffect(() => {
    setDetailTab('gifts');
    setTxPage(1);
    setTxSearch('');
    setTxMethod(METHOD_ANY);
    setTxDateRange({
      preset: 'allTime',
      ...resolvePreset('allTime', timezone),
    });
    setAdjustmentPage(1);
  }, [fund.id, timezone]);

  const transactions = useQuery({
    queryKey: [
      'giving',
      'contributions',
      'fund',
      fund.id,
      txPage,
      txSearch,
      txMethod,
      txDateRange.from,
      txDateRange.to,
    ],
    queryFn: () =>
      api.get<{
        data: FundTransaction[];
        page: OffsetPage;
        totals?: { amountMinor: number; count: number };
      }>('/giving/contributions', {
        query: {
          fundId: fund.id,
          page: txPage,
          pageSize: 20,
          q: txSearch.trim() || undefined,
          method: txMethod === METHOD_ANY ? undefined : txMethod,
          dateFrom: txDateRange.from ?? undefined,
          dateTo: txDateRange.to ?? undefined,
          includeVoided: true,
        },
      }),
    enabled: detailTab === 'gifts',
    placeholderData: keepPreviousData,
  });

  const typeSummary = useQuery({
    queryKey: ['giving', 'funds', fund.id, 'type-summary'],
    queryFn: () =>
      api.get<{
        data: Array<{ method: string; currency: string; amountMinor: number; count: number }>;
      }>(`/giving/funds/${fund.id}/type-summary`),
    enabled: detailTab === 'types',
  });

  const balanceAdjustments = useQuery({
    queryKey: ['giving', 'funds', fund.id, 'balance-adjustments', adjustmentPage],
    queryFn: () =>
      api.get<{
        data: FundBalanceAdjustment[];
        page: OffsetPage;
      }>(`/giving/funds/${fund.id}/balance-adjustments`, {
        query: {
          page: adjustmentPage,
          pageSize: 20,
        },
      }),
    enabled: detailTab === 'adjustments',
    placeholderData: keepPreviousData,
  });

  const voidContribution = useMutation({
    mutationFn: () =>
      api.post(`/giving/contributions/${voidTarget!.id}/void`, {
        reason: voidReason,
        password: voidPassword,
      }),
    onSuccess: () => {
      toast.success(t('voided'));
      setVoidTarget(null);
      setVoidReason('');
      setVoidPassword('');
      void queryClient.invalidateQueries({ queryKey: ['giving', 'contributions'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'insights'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'funds'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'funds', fund.id] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const transactionColumns: DataTableColumn<FundTransaction>[] = [
    {
      id: 'date',
      header: t('funds.detail.columns.date'),
      cell: (row) => formatDate(row.contributionDate),
    },
    {
      id: 'giver',
      header: t('funds.detail.columns.giver'),
      cell: (row) =>
        row.identityHidden ? (
          t('funds.detail.anonymous')
        ) : (
          <MemberNameLink memberId={row.memberId} name={row.giverName ?? ''} />
        ),
    },
    {
      id: 'amount',
      header: t('funds.detail.columns.amount'),
      money: (row) => ({ amount: row.amountMinor, currency: row.currency }),
    },
    {
      id: 'method',
      header: t('funds.detail.columns.method'),
      cell: (row) => t(`methods.${row.method}` as 'methods.cash'),
      hideBelowMd: true,
    },
    {
      id: 'receipt',
      header: t('funds.detail.columns.receipt'),
      cell: (row) => String(row.receiptNumber),
      align: 'right',
      hideBelowMd: true,
    },
    {
      id: 'status',
      header: t('funds.detail.columns.status'),
      cell: (row) =>
        row.isVoided ? (
          <Badge variant="danger">{t('contributions.voided')}</Badge>
        ) : (
          '—'
        ),
      hideBelowMd: true,
    },
  ];

  const adjustmentColumns: DataTableColumn<FundBalanceAdjustment>[] = [
    {
      id: 'date',
      header: t('funds.detail.columns.date'),
      cell: (row) => formatDateTime(row.createdAt),
    },
    {
      id: 'previousBalance',
      header: t('funds.detail.columns.previousBalance'),
      money: (row) => ({ amount: row.previousBalanceMinor, currency: row.currency }),
    },
    {
      id: 'newBalance',
      header: t('funds.detail.columns.newBalance'),
      money: (row) => ({ amount: row.newBalanceMinor, currency: row.currency }),
    },
    {
      id: 'adjustedBy',
      header: t('funds.detail.columns.adjustedBy'),
      cell: (row) => row.adjustedByName || '—',
    },
    {
      id: 'reason',
      header: t('funds.detail.columns.reason'),
      cell: (row) => row.reason,
      hideBelowMd: true,
    },
  ];

  const txPageMeta = transactions.data?.page;
  const adjustmentPageMeta = balanceAdjustments.data?.page;
  const summaryCurrency = fund.defaultCurrency || church?.baseCurrency || 'GHS';

  const giftFilterCount = useMemo(() => {
    let count = 0;
    if (txSearch.trim()) count += 1;
    if (txMethod !== METHOD_ANY) count += 1;
    if (txDateRange.preset !== 'allTime') count += 1;
    return count;
  }, [txDateRange.preset, txMethod, txSearch]);

  return (
    <div className="space-y-6">
      <ContributionDetailDialog
        contributionId={detailContributionId}
        open={Boolean(detailContributionId)}
        onOpenChange={(open) => {
          if (!open) setDetailContributionId(null);
        }}
      />

      <Dialog
        open={Boolean(voidTarget)}
        onOpenChange={(next) => {
          if (!next) {
            setVoidTarget(null);
            setVoidReason('');
            setVoidPassword('');
          }
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{t('contributions.voidTitle')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              voidContribution.mutate();
            }}
          >
            <Tip tipId="giving.contribution.void" />
            <div className="flex flex-col gap-1.5">
              <Label>{t('contributions.voidReason')}</Label>
              <Textarea
                value={voidReason}
                onChange={(event) => setVoidReason(event.target.value)}
                required
                rows={3}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fund-void-password">{t('contributions.voidPassword')}</Label>
              <Input
                id="fund-void-password"
                type="password"
                value={voidPassword}
                onChange={(event) => setVoidPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
              <p className="text-caption text-fg-secondary">{t('contributions.voidPasswordHint')}</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setVoidTarget(null);
                  setVoidReason('');
                  setVoidPassword('');
                }}
              >
                {tCommon('action.cancel')}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                loading={voidContribution.isPending}
                disabled={!voidReason.trim() || !voidPassword}
              >
                {t('contributions.voidConfirm')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <VoidReasonDialog
        open={Boolean(reasonTarget)}
        onClose={() => setReasonTarget(null)}
        reason={reasonTarget?.voidReason}
        voidedByName={reasonTarget?.voidedByName}
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_auto_auto]">
        <StatCard
          label={t('funds.detail.balance')}
          tipId="giving.funds.balance"
          money={{
            amount: fund.balanceMinor,
            currency: fund.defaultCurrency,
            parenthesiseNegative: false,
            colorBySign: true,
          }}
        />
        <StatCard
          label={t('funds.detail.total')}
          money={{
            amount: fund.givenMinor,
            currency: summaryCurrency,
          }}
        />
        <StatCard
          label={t('funds.detail.spent')}
          money={{
            amount: fund.spentMinor,
            currency: summaryCurrency,
          }}
        />
        <StatCard
          label={t('funds.detail.gifts')}
          value={fund.givenCount}
          className="min-w-[10.5rem]"
        />
        <div className="flex min-w-[5.5rem] flex-col justify-center rounded-lg border border-border px-3 py-2">
          <p className="text-caption text-fg-secondary">{t('funds.detail.currency')}</p>
          <p className="mt-1 text-body-sm font-medium text-fg">{fund.defaultCurrency}</p>
        </div>
      </div>

      <Tabs
        value={detailTab}
        onValueChange={(value) => {
          if (value === 'adjustments' || value === 'types') setDetailTab(value);
          else setDetailTab('gifts');
        }}
      >
        <TabsList>
          <TabsTrigger value="gifts">{t('funds.detail.tabs.gifts')}</TabsTrigger>
          <TabsTrigger value="types">{t('funds.detail.tabs.types')}</TabsTrigger>
          <TabsTrigger value="adjustments">{t('funds.detail.tabs.adjustments')}</TabsTrigger>
        </TabsList>

        <TabsContent value="gifts" className="mt-3 space-y-3">
          <FilterBar
            popoverPortal={popoverPortal}
            appliedCount={giftFilterCount}
            onClearAll={() => {
              setTxSearch('');
              setTxMethod(METHOD_ANY);
              setTxDateRange({
                preset: 'allTime',
                ...resolvePreset('allTime', timezone),
              });
              setTxPage(1);
            }}
            labels={{
              filters: t('funds.detail.filters.title'),
              clearAll: t('funds.detail.filters.clearAll'),
              applied: (count) => t('funds.detail.filters.applied', { count }),
            }}
            leading={
              <SearchInput
                value={txSearch}
                onChange={(value) => {
                  setTxSearch(value);
                  setTxPage(1);
                }}
                placeholder={t('funds.detail.filters.search')}
                clearLabel={tCommon('search.clear')}
                className="max-w-none sm:w-56"
                aria-label={t('funds.detail.filters.search')}
              />
            }
          >
            {({ popoverPortal: portal }) => (
              <>
                <DateRangeFilter
                  value={txDateRange}
                  onChange={(range) => {
                    setTxDateRange(range);
                    setTxPage(1);
                  }}
                  popoverPortal={portal}
                  labels={{
                    today: tCommon('date.today'),
                    yesterday: tCommon('date.yesterday'),
                    thisWeek: tCommon('date.thisWeek'),
                    lastWeek: tCommon('date.lastWeek'),
                    thisMonth: tCommon('date.thisMonth'),
                    lastMonth: tCommon('date.lastMonth'),
                    thisQuarter: tCommon('date.thisQuarter'),
                    thisYear: tCommon('date.thisYear'),
                    lastYear: tCommon('date.lastYear'),
                    allTime: tCommon('date.allTime'),
                    custom: tCommon('date.custom'),
                    from: t('form.dateFrom'),
                    to: t('form.dateTo'),
                  }}
                  presets={LIST_FILTER_DATE_RANGE_PRESETS}
                />
                <div className="w-44 min-w-[10rem]">
                  <Listbox
                    value={txMethod}
                    onChange={(value) => {
                      setTxMethod(value ?? METHOD_ANY);
                      setTxPage(1);
                    }}
                    options={[
                      {
                        value: METHOD_ANY,
                        label: t('funds.detail.filters.methodAny'),
                      },
                      ...CONTRIBUTION_METHODS.map((method) => ({
                        value: method,
                        label: t(`methods.${method}`),
                      })),
                    ]}
                    popoverPortal={portal}
                  />
                </div>
              </>
            )}
          </FilterBar>

          <DataTable
            rows={transactions.data?.data ?? []}
            columns={transactionColumns}
            rowKey={(row) => row.id}
            loading={transactions.isLoading}
            onRowClick={(row) => setDetailContributionId(row.id)}
            empty={
              <p className="text-body-sm text-fg-secondary">
                {giftFilterCount > 0
                  ? t('funds.detail.transactionsEmptyFiltered')
                  : t('funds.detail.transactionsEmpty')}
              </p>
            }
            rowActions={(row) =>
              row.isVoided ? (
                <Button size="sm" variant="outline" onClick={() => setReasonTarget(row)}>
                  {t('contributions.viewReason')}
                </Button>
              ) : row.canVoid ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setVoidTarget(row)}
                >
                  {t('contributions.void')}
                </Button>
              ) : null
            }
            actionsHeader={tCommon('table.actions')}
          />

          {txPageMeta && txPageMeta.totalPages > 1 ? (
            <Pagination
              page={txPageMeta.page}
              pageSize={txPageMeta.pageSize}
              totalItems={txPageMeta.totalItems}
              totalPages={txPageMeta.totalPages}
              onPageChange={setTxPage}
              labels={{
                showing: ({ from, to, total }) =>
                  tCommon('pagination.showing', { from, to, total }),
                perPage: tCommon('pagination.perPage'),
                first: tCommon('pagination.first'),
                previous: tCommon('pagination.previous'),
                next: tCommon('pagination.next'),
                last: tCommon('pagination.last'),
              }}
            />
          ) : null}
        </TabsContent>


        <TabsContent value="types" className="mt-3">
          <DataTable
            rows={typeSummary.data?.data ?? []}
            columns={[
              {
                id: 'method',
                header: t('funds.detail.columns.method'),
                cell: (row) => t(`methods.${row.method}` as 'methods.cash'),
              },
              {
                id: 'amount',
                header: t('funds.detail.columns.amount'),
                align: 'right',
                money: (row) => ({ amount: row.amountMinor, currency: row.currency }),
              },
              {
                id: 'count',
                header: t('funds.detail.columns.gifts'),
                align: 'right',
                cell: (row) => String(row.count),
              },
            ]}
            rowKey={(row) => `${row.method}-${row.currency}`}
            loading={typeSummary.isLoading}
            empty={
              <p className="text-body-sm text-fg-secondary">{t('funds.detail.typesEmpty')}</p>
            }
          />
        </TabsContent>

        <TabsContent value="adjustments" className="mt-3 space-y-3">
          <DataTable
            rows={balanceAdjustments.data?.data ?? []}
            columns={adjustmentColumns}
            rowKey={(row) => row.id}
            loading={balanceAdjustments.isLoading}
            empty={
              <p className="text-body-sm text-fg-secondary">
                {t('funds.detail.adjustmentsEmpty')}
              </p>
            }
          />

          {adjustmentPageMeta && adjustmentPageMeta.totalPages > 1 ? (
            <Pagination
              page={adjustmentPageMeta.page}
              pageSize={adjustmentPageMeta.pageSize}
              totalItems={adjustmentPageMeta.totalItems}
              totalPages={adjustmentPageMeta.totalPages}
              onPageChange={setAdjustmentPage}
              labels={{
                showing: ({ from, to, total }) =>
                  tCommon('pagination.showing', { from, to, total }),
                perPage: tCommon('pagination.perPage'),
                first: tCommon('pagination.first'),
                previous: tCommon('pagination.previous'),
                next: tCommon('pagination.next'),
                last: tCommon('pagination.last'),
              }}
            />
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  );
};
